import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Twitter, Facebook, Youtube } from "lucide-react";
import { OFFICIAL_LOGO, EVENT_TAGLINE, SPORTSLINK_LOGO, SPORTSLINK_TAGLINE, INDIVIDUAL_EVENT_COUNT, RELAY_EVENT_COUNT } from "@/lib/brand";

// Account registration closes on 29 Sep 2026 23:59 IST
const currentHour = new Date().getHours();
const REGISTRATION_CLOSED = currentHour < 6 || currentHour >= 18;

const ContactLine = ({ href, primary, secondary }) => (
  <a
    href={href}
    className="group/line block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200"
  >
    <span className="group-hover/line:underline underline-offset-2">{primary}</span>
    {secondary && <span className="text-[#94A3B8] group-hover/line:text-[#00F0FF]/60"> — {secondary}</span>}
  </a>
);

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-gradient-to-b from-[#0B1429] to-[#050914] mt-24 overflow-hidden">
      {/* Top hairline with a soft aqua glow, instead of a flat border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/40 to-transparent" />
      {/* Faint ambient glow, matches the aqua accent used throughout the site */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[#00F0FF]/[0.04] blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg p-3 inline-block mb-5 shadow-[0_0_32px_rgba(0,240,255,0.15)]">
            <img src={OFFICIAL_LOGO} alt="2nd Asian Masters Swimming Championships 2026" className="h-24 w-24 object-contain" />
          </div>
          <div className="mb-4">
            <div className="font-display font-black text-white text-sm tracking-[0.15em]">2ND ASIAN MASTERS</div>
            <div className="font-display font-bold text-[#00F0FF] text-sm tracking-wider">SWIMMING CHAMPIONSHIPS · 2026</div>
            <div className="text-[10px] text-[#94A3B8] uppercase tracking-[0.2em] mt-1.5">Bengaluru · India</div>
          </div>
          <p className="text-[#CBD5E1] text-sm leading-relaxed italic mb-4">
            "{EVENT_TAGLINE}"
          </p>
          <p className="text-[#94A3B8] text-sm leading-relaxed">
            Hosted by Swimming Federation of India under Asia Aquatics. Athletes from 25 to 80+ competing across {INDIVIDUAL_EVENT_COUNT} events and {RELAY_EVENT_COUNT} relays.
          </p>
          <div className="flex gap-3 mt-6">
            {[
              { Icon: Instagram, label: "Instagram" },
              { Icon: Twitter, label: "Twitter" },
              { Icon: Facebook, label: "Facebook" },
              { Icon: Youtube, label: "YouTube" },
            ].map(({ Icon, label }, i) => (
              <a key={i} href="#" aria-label={label} data-testid={`social-${i}`} className="w-11 h-11 border border-white/10 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF] hover:shadow-[0_0_16px_rgba(0,240,255,0.25)] transition-all duration-200 touch-manipulation">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Quick Links</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Home</Link></li>
            <li><a href="/#about" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">About</a></li>
            {!REGISTRATION_CLOSED && (
              <li><Link to="/register" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Athlete Registration</Link></li>
            )}
            <li><Link to="/city-guide" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Bengaluru Guide</Link></li>
            <li><a href="/#faq" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">FAQ</a></li>
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Policies</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/terms-and-conditions" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Terms & Conditions</Link></li>
            <li><Link to="/privacy-policy" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Privacy Policy</Link></li>
            <li><Link to="/cookie-policy" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Cookie Policy</Link></li>
            <li><Link to="/refund-policy" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Refund Policy</Link></li>
            <li><a href="#" className="inline-block py-2 md:py-0 text-white/70 hover:text-[#00F0FF] transition-colors duration-200">Code of Conduct</a></li>
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Contact</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                <Mail className="w-3.5 h-3.5 text-[#00F0FF]" />
              </span>
              <span className="flex flex-col gap-1.5 pt-1">
                <ContactLine href="mailto:support@asianmastersbangalore2026.com" primary="support@asianmastersbangalore2026.com" />
                <ContactLine href="mailto:tech@asianmastersbangalore2026.com" primary="tech@asianmastersbangalore2026.com" secondary="Technical queries" />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                <Phone className="w-3.5 h-3.5 text-[#00F0FF]" />
              </span>
              <span className="flex flex-col gap-1.5 pt-1">
                <ContactLine href="tel:+917625017033" primary="+91 76250 17033" secondary="Support" />
                <ContactLine href="tel:+917625062033" primary="+91 76250 62033" secondary="Visa" />
                <ContactLine href="tel:+919353154449" primary="+91 93531 54449" secondary="Org Sec" />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <MapPin className="w-3.5 h-3.5 text-[#00F0FF]" />
              </span>
              <span className="text-white/70 pt-1.5">Centre for Sports Excellence · Dolphin Aquatics, Bengaluru</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/[0.08] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[#94A3B8] text-center md:text-left">
            © 2026 2nd Asian Masters Swimming Championships · "{EVENT_TAGLINE}"
          </div>
          <a
            href="#"
            data-testid="sportslink-credit"
            className="group flex items-center gap-3 text-[#94A3B8] hover:text-white transition-colors duration-200"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">Powered by</span>
            <div className="flex items-center gap-2">
              <img
                src={SPORTSLINK_LOGO}
                alt="SportsLink"
                className="h-9 w-9 rounded-full ring-1 ring-white/10 group-hover:ring-[#00F0FF]/50 transition"
              />
              <div className="leading-tight">
                <div className="font-display font-black text-sm tracking-tight text-white">
                  sports<span className="text-[#00F0FF]">link</span>
                </div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#94A3B8] group-hover:text-[#00F0FF] transition">
                  {SPORTSLINK_TAGLINE}
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </footer>
  );
}
